import type { Locale, SpotDetail, Status } from "@divespots/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, History, Info } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router";
import { api } from "../api/client.ts";
import { isApiRequestError } from "../api/errors.ts";
import { useLakes } from "../api/index.ts";
import { Button } from "../components/Button.tsx";
import { useLocale } from "../i18n/index.ts";
import { useSession } from "./session.ts";
import {
  emptySpotForm,
  isFormDirty,
  issuesToErrors,
  mergeSaved,
  spotToForm,
  validateSpotCreate,
  validateSpotPatch,
} from "./forms.ts";
import type { FieldErrors, Issue, SpotForm } from "./forms.ts";
import { ObjectsSection } from "./ObjectsSection.tsx";
import { SpotFields } from "./SpotFields.tsx";
import { DiscardSheet, useUnsavedGuard } from "./unsaved.tsx";
import { ConfirmSheet, Field, Section } from "./ui.tsx";

const STATUSES: Status[] = ["draft", "published", "pending"];

class FormInvalid extends Error {
  constructor(readonly errors: FieldErrors) {
    super("invalid");
  }
}

/** `/admin/spots/new` and `/admin/spots/:id`. */
export function SpotFormPage() {
  const { id } = useParams();
  const locale = useLocale();
  const { t } = useTranslation();
  const isNew = id === undefined;

  // Edit: the list gives id -> slug, the detail gives every field (and drafts, for moderators).
  const list = useQuery({
    queryKey: ["admin", "spots"],
    queryFn: ({ signal }) => api.listSpots({}, signal).then((r) => r.spots),
    enabled: !isNew,
  });
  const slug = list.data?.find((s) => s.id === id)?.slug;
  const detail = useQuery({
    queryKey: ["admin", "spot", id],
    queryFn: ({ signal }) => api.getSpot(slug as string, signal),
    enabled: Boolean(slug),
  });

  if (isNew) return <SpotEditor original={null} />;
  if (list.isError || detail.isError) {
    return (
      <p className="adm-error" role="alert">
        {t("admin.err.load")}
      </p>
    );
  }
  if (list.isSuccess && !slug) {
    return (
      <div className="grid gap-3">
        <h1 className="ds-text-title-1 m-0">{t("admin.spots.notFound")}</h1>
        <Link className="ds-btn ds-btn--tinted" to={`/${locale}/admin/spots`}>
          {t("admin.spots.backToList")}
        </Link>
      </div>
    );
  }
  if (!detail.data) return <p role="status">{t("common.loading")}</p>;
  return <SpotEditor key={detail.data.id} original={detail.data} />;
}

export function SpotEditor({ original }: { original: SpotDetail | null }) {
  const { t } = useTranslation();
  const locale = useLocale();
  const isAdmin = useSession().user?.role === "admin";
  const navigate = useNavigate();
  const qc = useQueryClient();
  const lakes = useLakes();

  const [form, setForm] = useState<SpotForm>(() =>
    original ? spotToForm(original) : emptySpotForm(),
  );
  const formRef = useRef(form);
  useEffect(() => {
    formRef.current = form;
  }, [form]);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [banner, setBanner] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [foreign, setForeign] = useState(false);
  const submitted = useRef<{ snapshot: SpotForm; sent: SpotForm; before: SpotForm } | null>(null);
  const [textLocale, setTextLocale] = useState<Locale>("de");
  const [confirmDelete, setConfirmDelete] = useState(false);

  const set = <K extends keyof SpotForm>(k: K, v: SpotForm[K]) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: "" }));
    setSaved(false);
    setForeign(false);
  };

  const baseline = useMemo(() => (original ? spotToForm(original) : emptySpotForm()), [original]);
  const dirty = isFormDirty(form, baseline);
  const { blocker, allowLeave } = useUnsavedGuard(dirty);

  const invalidate = async () => {
    await qc.invalidateQueries({ queryKey: ["admin"] });
    await qc.invalidateQueries({ queryKey: ["spots"] });
    await qc.invalidateQueries({ queryKey: ["spot"] });
  };

  const onError = (e: unknown) => {
    setSaved(false);
    if (e instanceof FormInvalid) {
      setBanner(t("admin.err.fix"));
      return setErrors(e.errors);
    }
    if (isApiRequestError(e)) {
      if (e.code === "conflict") {
        setBanner(null);
        return setErrors({ slug: t("admin.err.slugTaken") });
      }
      if (e.code === "bad_request" && Array.isArray(e.details)) {
        setBanner(t("admin.err.fix"));
        return setErrors(issuesToErrors(e.details as Issue[], t, true));
      }
      if (e.code === "forbidden" || e.code === "unauthorized")
        return setBanner(t("admin.err.auth"));
    }
    setBanner(t("admin.err.save"));
  };

  const save = useMutation({
    mutationFn: async (status?: Status) => {
      const next = status ? { ...form, status } : form;
      submitted.current = { snapshot: form, sent: next, before: baseline };
      if (original) {
        const v = validateSpotPatch(original, next, t);
        if (!v.ok) throw new FormInvalid(v.errors);
        if (Object.keys(v.data).length === 0) return original;
        return api.updateSpot(original.id, v.data);
      }
      const v = validateSpotCreate(next, t);
      if (!v.ok) throw new FormInvalid(v.errors);
      return api.createSpot(v.data);
    },
    onSuccess: async (spot) => {
      setErrors({});
      setBanner(null);
      if (!original) {
        allowLeave();
        await invalidate();
        void navigate(`/${locale}/admin/spots/${spot.id}`, { replace: true });
        return;
      }
      // Show what the server now has: edits of others in fields this save did not touch included.
      const sub = submitted.current;
      if (sub && spot !== original) {
        const after = spotToForm(spot);
        const m = mergeSaved({
          current: formRef.current,
          snapshot: sub.snapshot,
          sent: sub.sent,
          before: sub.before,
          after,
        });
        setForm(m.form);
        setForeign(m.foreign);
      }
      setSaved(true);
      await invalidate();
    },
    onError,
  });

  const del = useMutation({
    mutationFn: () => api.deleteSpot((original as SpotDetail).id),
    onSuccess: async () => {
      allowLeave();
      await invalidate();
      void navigate(`/${locale}/admin/spots`);
    },
  });

  // Keep the form's status in sync after a publish/unpublish round trip.
  useEffect(() => {
    if (original) setForm((f) => ({ ...f, status: original.status }));
  }, [original?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const lakeName = lakes.data?.find((l) => l.slug === original?.lakeSlug);

  return (
    <div className="grid gap-6">
      <div className="adm-pagehead">
        <h1 className="ds-text-title-1 m-0">
          {original ? t("admin.spots.edit", { name: original.name }) : t("admin.spots.new")}
        </h1>
        {original ? (
          <div className="adm-pagehead__links">
            {isAdmin ? (
              <Link
                className="ds-btn ds-btn--plain"
                to={`/${locale}/admin/protokoll?spotId=${original.id}`}
              >
                <History aria-hidden="true" strokeWidth={1.75} />
                {t("admin.audit.history")}
              </Link>
            ) : null}
            <Link className="ds-btn ds-btn--plain" to={`/${locale}/spot/${original.slug}`}>
              <ExternalLink aria-hidden="true" strokeWidth={1.75} />
              {t("admin.spots.viewPublic")}
            </Link>
          </div>
        ) : null}
      </div>

      <form
        className="grid gap-6"
        noValidate
        aria-label={t("admin.spots.formLabel")}
        onSubmit={(e) => {
          e.preventDefault();
          setBanner(null);
          save.mutate(undefined);
        }}
      >
        <SpotFields
          mode="staff"
          form={form}
          errors={errors}
          set={set}
          setForm={setForm}
          setErrors={setErrors}
          onEdited={() => setSaved(false)}
          textLocale={textLocale}
          setTextLocale={setTextLocale}
          basicsExtra={
            original ? (
              <p className="ds-text-footnote text-label-secondary m-0">
                {lakeName
                  ? t("admin.field.lake", { name: lakeName.slug })
                  : t("admin.field.noLake")}{" "}
                · {original.hasBathy ? t("admin.field.hasBathy") : t("spot.noBathy")}
              </p>
            ) : null
          }
        />

        <Section title={t("admin.section.publication")}>
          <Field label={t("admin.field.status")}>
            {({ id }) => (
              <select
                id={id}
                className="adm-input"
                value={form.status}
                onChange={(e) => set("status", e.target.value as Status)}
              >
                {STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {t(`admin.status.${s}`)}
                  </option>
                ))}
              </select>
            )}
          </Field>
          {original?.status === "draft" ? (
            <p className="ds-text-footnote text-label-secondary m-0">
              {t("admin.spots.draftNote")}
            </p>
          ) : null}
        </Section>

        <div className="adm-actions adm-actions--sticky">
          {banner ? (
            <p className="adm-error m-0" role="alert">
              {banner}
            </p>
          ) : null}
          {saved ? (
            <p className="adm-ok m-0" role="status">
              {t("admin.common.saved")}
            </p>
          ) : null}
          {foreign ? (
            <p className="adm-warning m-0" role="status">
              <Info aria-hidden="true" strokeWidth={1.75} size={18} />
              {t("admin.err.concurrent")}
            </p>
          ) : null}
          <Button type="submit" variant="filled" disabled={save.isPending}>
            {original ? t("admin.common.save") : t("admin.spots.create")}
          </Button>
          {original && original.status !== "published" ? (
            <Button
              variant="tinted"
              disabled={save.isPending}
              onClick={() => save.mutate("published")}
            >
              {t("admin.spots.publish")}
            </Button>
          ) : null}
          {original && original.status === "published" ? (
            <Button variant="tinted" disabled={save.isPending} onClick={() => save.mutate("draft")}>
              {t("admin.spots.unpublish")}
            </Button>
          ) : null}
          {original ? (
            <Button variant="destructive" onClick={() => setConfirmDelete(true)}>
              {t("admin.spots.delete")}
            </Button>
          ) : null}
        </div>
      </form>

      {original ? (
        <ObjectsSection spot={original} />
      ) : (
        <p className="ds-text-subheadline text-label-secondary m-0">
          {t("admin.objects.afterCreate")}
        </p>
      )}

      <DiscardSheet blocker={blocker} />

      {confirmDelete && original ? (
        <ConfirmSheet
          title={t("admin.spots.deleteTitle")}
          body={t("admin.spots.deleteBody", { name: original.name })}
          confirmLabel={t("admin.spots.deleteConfirm")}
          busy={del.isPending}
          error={del.isError ? t("admin.err.save") : undefined}
          onConfirm={() => del.mutate()}
          onCancel={() => setConfirmDelete(false)}
        />
      ) : null}
    </div>
  );
}
